import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';

const Container = styled.div`
  position: relative;
  flex-shrink: 0;
  font-family: Raleway;
  font-size: 13px;
  button {
    border: 0;
    border-radius: 6px;
    background: #f3ecff;
    color: #9738ff;
    cursor: pointer;
    padding: 5px 10px;
    min-height: 28px;
  }
  button:focus-visible {
    outline: 2px solid #9738ff;
    outline-offset: 2px;
  }
  button:disabled {
    cursor: wait;
    opacity: 0.6;
  }
  [role='menu'] {
    position: absolute;
    right: 0;
    top: 34px;
    z-index: 20;
    width: 190px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: white;
    box-shadow: 0 4px 16px #0002;
    padding: 4px;
    button {
      width: 100%;
      text-align: left;
      background: white;
      color: #333;
    }
    button:hover {
      background: #f5f5f5;
    }
    [role='alert'] {
      padding: 8px;
      font-size: 12px;
      color: #a1243b;
    }
  }
`;

export const validImageId = (id: string | number) =>
  /^[1-9]\d*$/.test(String(id));

const ImageDownloadMenu = ({ imageId }: { imageId: string | number }) => {
  const { sequence, apiService } = useEditorStore();
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const trigger = useRef<HTMLButtonElement>(null);
  if (!sequence?.internalSettings || !apiService || !validImageId(imageId))
    return null;

  const download = async () => {
    setDownloading(true);
    setError('');
    try {
      const result = await apiService.getPictureDownload(
        Number(imageId),
        sequence.id
      );
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setOpen(false);
      trigger.current?.focus();
    } catch {
      setError('Unable to download image. Try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Container
      onClick={event => event.stopPropagation()}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setOpen(false);
      }}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          setOpen(false);
          trigger.current?.focus();
        }
      }}
    >
      <button
        type="button"
        ref={trigger}
        aria-label="Image actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        ⋮
      </button>
      {open && (
        <div role="menu" aria-label="Image actions">
          <button
            type="button"
            role="menuitem"
            autoFocus
            disabled={downloading}
            onClick={download}
          >
            {downloading ? 'Downloading…' : 'Download image'}
          </button>
          {error && <div role="alert">{error}</div>}
        </div>
      )}
    </Container>
  );
};
export default ImageDownloadMenu;
